<!--
FranchiseSelect Component - Reusable Franchise Selection Component

USE CASES:
==========
- Use in any form where franchise selection is needed
- Automatically loads franchises selected in the global filter (useFiltro)
- Respects filter changes and updates dynamically
- Supports v-model binding
- Handles franchise change callbacks
- Auto-selects first available franchise if no value is provided

IMPORTANT:
===========
- This component shows ONLY franchises selected in the filter, not all franchises under user's permissions
- Automatically refreshes when filter is updated (listens to 'filter.updated' event)
- Waits for filter initialization before loading franchises

PROPS:
=======
- modelValue: Current selected franchise ID (v-model)
- elemId: Unique identifier for the select element
- label: Label text for the select (defaults to 'Franquia')
- placeholder: Placeholder text (defaults to 'Clique para escolher')
- callback: Optional function to call when franchise changes
- disabled: Boolean to disable the select
- includeIds: optional number[] — when set, only those franchise IDs are offered

EVENTS:
========
- update:modelValue: Emitted when franchise selection changes
- franchise-change: Emitted with franchise ID when selection changes
- franchise-data: Emitted with complete franchise data (including Pais) when selection changes
-->

<template>
  <select-list
    :label="label"
    :elem-id="elemId"
    :itens-select="franquias"
    :model-value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :data-testid="`${elemId}-select`"
    :callback="handleFranchiseChange"
    @update:model-value="handleValueChange"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../utils/DebugLogger';
import { useFiltro } from '../composables/useFiltro';
import { useMyFranchises } from '../composables/useMyFranchises';
import { eventBus } from '../eventBus';
import { permissionsService, type FranquiaData } from '../services/permissionsService';
import SelectList from './SelectList.vue';

interface SelectItem {
  ID: string | number;
  Name: string;
}

// Props
const props = defineProps({
  // Current selected franchise ID (v-model)
  modelValue: {
    type: [String, Number] as any,
    default: ''
  },

  // Unique identifier for the select element
  elemId: {
    type: String,
    default: 'franchise-select'
  },

  // Label text for the select
  label: {
    type: String,
    default: ''
  },

  // Placeholder text
  placeholder: {
    type: String,
    default: ''
  },

  // Optional callback function triggered when franchise changes
  callback: {
    type: Function as any,
    default: null
  },

  // Whether the select is disabled
  disabled: {
    type: Boolean,
    default: false
  },

  // When set, only these franchise IDs are offered. Undefined = no extra filter.
  includeIds: {
    type: Array as () => number[] | undefined,
    default: undefined
  }
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: string | number];
  'franchise-change': [franchiseId: string | number];
  'franchise-data': [data: FranquiaData]; // Complete franchise data including Pais for internationalization
}>();

// Composables
const { t } = useI18n();
const { getSelectedFranchisesSelectArray, initialized } = useFiltro();
const { rows: myFranchises, fetchMyFranchises } = useMyFranchises();

// Reactive data
const franquias = ref<SelectItem[]>([]);
const isLoading = ref<boolean>(false);
const hasAutoSelected = ref<boolean>(false); // Track if we've already auto-selected once

// Computed
const computedLabel = computed((): string => {
  return props.label || t('Franquia');
});

const computedPlaceholder = computed((): string => {
  return props.placeholder || t('Clique para escolher');
});

// Methods
/**
 * Announce a PRESELECTED franchise.
 *
 * Neither of FranchiseSelect's other emit sites fires for a value that was
 * already present at mount: the auto-select branch is guarded by !hasValue, and
 * watch(modelValue) has no `immediate`, so it only reacts to CHANGES. Every
 * "edit" form and anything opened from the staff 360 therefore received no
 * franchise-data at all, and consumers that derive from it — this component's
 * own docblock calls it "critical for edit mode": document config, country
 * settings — silently fell back to their unknown-value behaviour.
 *
 * The setup-fee modal made it visible: with no country it offered a Chilean
 * franchise BRL, CLP and USD markets, three of which the backend refuses.
 *
 * Called explicitly rather than by making the watcher `immediate`, which would
 * also fire on every re-mount and filter-driven reload, double-emitting into
 * the ~25 domains that use this component.
 */
const emitPreselectedFranchiseData = (value: string | number): void => {
  const preselectedData = permissionsService.getFranquiaById(value);
  if (preselectedData) {
    log('FranchiseSelect', 'emitPreselectedFranchiseData', 'Emitting franchise data for preselected value:', preselectedData);
    emit('franchise-data', preselectedData);
  } else {
    // Not in the permissions payload — out of scope, or a stale cache. Say
    // nothing rather than something wrong: a consumer that fails CLOSED on an
    // unknown franchise is safer than one fed a guess.
    log('FranchiseSelect', 'emitPreselectedFranchiseData', 'Preselected franchise not in permissions payload', value);
  }
};

const hasPresetValue = (value: unknown): value is string | number =>
  value !== '' && value !== null && value !== undefined;

const loadFranquias = async (): Promise<void> => {
  log('FranchiseSelect', 'loadFranquias', 'Loading franquias from filter');

  isLoading.value = true;

  try {
    // Wait for filter to be initialized
    if (!initialized.value) {
      log('FranchiseSelect', 'loadFranquias', 'Filter not initialized yet, waiting...');
      franquias.value = [];
      isLoading.value = false;
      return;
    }

    // Get selected franchises from filter
    let selectedFranchises = await getSelectedFranchisesSelectArray();

    // Fallback to my-franchises endpoint when filter is empty
    // This handles new users with no filter-scoped franchises but who own franchises
    // via permissions (e.g., minhas-assinaturas page for ungated users)
    if (!selectedFranchises || selectedFranchises.length === 0) {
      log('FranchiseSelect', 'loadFranquias', 'Filter returned no franchises, trying my-franchises endpoint');
      try {
        await fetchMyFranchises();
        // Convert my-franchises rows to select format
        selectedFranchises = myFranchises.value.map((row) => ({
          ID: row.franchise_id,
          Name: row.franchise_name,
        }));
        log('FranchiseSelect', 'loadFranquias', 'Loaded franchises from my-franchises:', selectedFranchises);
      } catch (err) {
        log('FranchiseSelect', 'loadFranquias', 'Error fetching my-franchises, treating as empty', err);
        selectedFranchises = [];
      }
    }

    if (!selectedFranchises || selectedFranchises.length === 0) {
      log('FranchiseSelect', 'loadFranquias', 'No franchises available (filter + my-franchises both empty)');
      franquias.value = [];
      // An empty OPTION LIST does not mean an unknown franchise: a preselected
      // value still resolves from the permissions payload, and consumers that
      // derive from franchise-data need it either way. Returning silently here
      // left them with nothing at all.
      if (hasPresetValue(props.modelValue)) {
        emitPreselectedFranchiseData(props.modelValue);
      }
      isLoading.value = false;
      return;
    }

    if (props.includeIds != null) {
      const allowed = new Set(props.includeIds.map(Number));
      selectedFranchises = selectedFranchises.filter((f) => allowed.has(Number(f.ID)));
    }

    franquias.value = selectedFranchises;
    log('FranchiseSelect', 'loadFranquias', 'Loaded selected franquias:', franquias.value);

    // Auto-select first franchise ONLY if:
    // 1. No value is currently set
    // 2. We haven't auto-selected before (prevents re-triggering on filter updates)
    const currentValue = props.modelValue;
    const hasValue = currentValue !== '' && currentValue !== null && currentValue !== undefined;

    if (!hasValue && !hasAutoSelected.value && franquias.value.length > 0) {
      const firstFranchiseId = franquias.value[0].ID;
      hasAutoSelected.value = true; // Mark that we've done auto-selection

      emit('update:modelValue', firstFranchiseId);
      emit('franchise-change', firstFranchiseId);

      // Emit complete franchise data (including Pais) for internationalization support
      const franquiaData = permissionsService.getFranquiaById(firstFranchiseId);
      if (franquiaData) {
        log('FranchiseSelect', 'loadFranquias', 'Emitting franchise data:', franquiaData);
        emit('franchise-data', franquiaData);
      }

      // Call callback if provided
      if (props.callback && typeof props.callback === 'function') {
        (props.callback as (id: string | number) => void)(firstFranchiseId);
      }

      log('FranchiseSelect', 'loadFranquias', 'Auto-selected first franchise:', firstFranchiseId);
    } else if (hasValue) {
      emitPreselectedFranchiseData(currentValue);
    } else {
      log('FranchiseSelect', 'loadFranquias', 'Skipping auto-select', { hasValue, hasAutoSelected: hasAutoSelected.value });
    }
  } catch (error) {
    log('FranchiseSelect', 'loadFranquias', 'Error loading franquias:', error);
    franquias.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleFranchiseChange = (franchiseId: string | number): void => {
  log('FranchiseSelect', 'handleFranchiseChange', 'Franchise changed to:', franchiseId);

  emit('franchise-change', franchiseId);

  // Emit complete franchise data (including Pais) for internationalization support
  const franquiaData = permissionsService.getFranquiaById(franchiseId);
  if (franquiaData) {
    log('FranchiseSelect', 'handleFranchiseChange', 'Emitting franchise data:', franquiaData);
    emit('franchise-data', franquiaData);
  }

  // Call callback if provided
  if (props.callback && typeof props.callback === 'function') {
    (props.callback as (id: string | number) => void)(franchiseId);
  }
};

const handleValueChange = (value: string | number): void => {
  log('FranchiseSelect', 'handleValueChange', 'Value changed to:', value);
  emit('update:modelValue', value);
};

// Watch for modelValue changes from parent (e.g., when loading data for edit)
watch(() => props.modelValue, (newValue: string | number) => {
  log('FranchiseSelect', 'watch:modelValue', 'ModelValue changed from parent:', newValue);

  // If parent explicitly sets a value, reset hasAutoSelected flag
  // This allows proper behavior when switching between new/edit modes
  if (newValue !== '' && newValue !== null && newValue !== undefined) {
    hasAutoSelected.value = true; // Prevent auto-selection from overriding this value

    // Emit franchise data and call callback so the form can load
    // document config, country settings, etc. (critical for edit mode)
    const franquiaData = permissionsService.getFranquiaById(newValue);
    if (franquiaData) {
      log('FranchiseSelect', 'watch:modelValue', 'Emitting franchise data for edit:', franquiaData);
      emit('franchise-data', franquiaData);
    }
    if (props.callback && typeof props.callback === 'function') {
      (props.callback as (id: string | number) => void)(newValue);
    }
  }
});

watch(
  () => (props.includeIds ?? []).join(','),
  () => {
    if (initialized.value) {
      loadFranquias();
    }
  },
);

// Watch for filter to be initialized
watch(() => initialized.value, (isInitialized: boolean) => {
  if (isInitialized) {
    log('FranchiseSelect', 'watch:initialized', 'Filter initialized, loading franquias');
    loadFranquias();
  }
}, { immediate: true });

// Listen to filter updates
eventBus.on('filter.updated', () => {
  log('FranchiseSelect', 'event:filter.updated', 'Filter updated, reloading franquias');
  loadFranquias();
});

// Lifecycle
onMounted(() => {
  log('FranchiseSelect', 'mounted', 'Component mounted');
  // loadFranquias will be called by the watcher when filter is ready
});
</script>

<style scoped>
/* No additional styles needed - inherits from SelectList */
</style>
